import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { useAuth } from '../context/AuthContext';
import { useTheme, JADE_DARK, JADE_LIGHT } from '../context/ThemeContext';
import { useBreakpoints } from '../hooks/useWindowWidth';
import LoginModal from '../components/LoginModal';
import LandingNavbar from '../components/landing/LandingNavbar';
import Magnet from '../components/landing/Magnet';
import useGsapStagger from '../components/landing/useGsapStagger';
import { COMMUNITY_URL, COMMUNITY_SIZE } from '../constants/community';

const LP = {
  heading: "'Bricolage Grotesque', system-ui, sans-serif",
  body:    "'Source Serif 4', Georgia, serif",
  data:    "'JetBrains Mono', monospace",
};

// ── Reveal single element on scroll ─────────────────────────────────────────
function useReveal(cls = 'lp-reveal', threshold = 0.12) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('lp-visible'); obs.unobserve(el); } },
      { threshold }
    );
    el.classList.add(cls);
    obs.observe(el);
    return () => obs.disconnect();
  }, [cls, threshold]);
  return ref;
}

// ── Count-up animation for numeric stats ────────────────────────────────────
function useCountUp(target, duration = 1600) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || done.current) return;
      done.current = true;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVal(target); return; }
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setVal(Math.round(eased * target));
        if (t < 1) requestAnimationFrame(tick);
        else setVal(target);
      };
      requestAnimationFrame(tick);
      obs.unobserve(el);
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return [ref, val];
}

// Interpolates between two "r,g,b" strings — used to darken the network's
// gold as it crosses from the dark Forest zone into the light Parchment
// zone below the seam's glow, where the same bright gold reads as washed
// out against a near-white backdrop.
function lerpRgbStr(rgbA, rgbB, t) {
  const a = rgbA.split(',').map(Number);
  const b = rgbB.split(',').map(Number);
  return `${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(a[2] + (b[2] - a[2]) * t)}`;
}

// ── Animated neural network background ──────────────────────────────────────
// nerveRgb is the color used through the dark stretch; nerveRgbEnd (if given)
// is what nodes ease toward in the bottom ~22% of the canvas, where this
// canvas now runs past the seam's glow into a lightening backdrop — without
// it, nodes drawn in bright dark-mode gold nearly disappear against cream.
function NervesCanvas({ nerveRgb, nerveRgbEnd, interactive = true }) {
  const canvasRef   = useRef(null);
  const stateRef    = useRef({ nodes: [], renderNodes: [], width: 0, height: 0 });
  const mouseRef    = useRef({ x: -9999, y: -9999, active: 0, target: 0 });
  const nerveRgbRef = useRef(nerveRgb);
  useEffect(() => { nerveRgbRef.current = nerveRgb; }, [nerveRgb]);
  const nerveRgbEndRef = useRef(nerveRgbEnd);
  useEffect(() => { nerveRgbEndRef.current = nerveRgbEnd; }, [nerveRgbEnd]);
  // Read fresh at event time (not captured once at mount) so this stays
  // correct if the viewport crosses the mobile breakpoint mid-session.
  const interactiveRef = useRef(interactive);
  useEffect(() => { interactiveRef.current = interactive; }, [interactive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    const LINK_DIST  = 160;
    const COUNT      = 125; // canvas now spans Hero + the seam below it, ~40% more area than Hero alone — bumped from 90 to keep node density the same
    const REPEL_R    = 140; // px around the cursor that pushes nodes out of the way
    const MAX_PUSH   = 30;  // max px a node bulges at the cursor's center
    const SYNAPSE_R  = 220; // px radius that draws a "synapse" line to the cursor

    // Mobile browsers change window.innerHeight as you scroll (the address
    // bar collapsing/expanding), and this section's height is in vh units —
    // so that alone fires a real `resize` event on almost every scroll
    // tick. Regenerating all 90 nodes at fresh random positions on every
    // one of those (the old behavior) made the network visibly teleport in
    // rapid bursts while scrolling — that's the "moving full fast" glitch.
    // Fix: only width changes warrant a real reset (an actual rotation/
    // resize); a height-only change just clamps the existing nodes into
    // the new bounds so nothing jumps. Also debounced, so a drag-resize on
    // desktop doesn't thrash it either.
    let lastWidth = null;
    let resizeTimer = null;
    const applyResize = () => {
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      canvas.width = w; canvas.height = h;

      const widthChanged = lastWidth === null || Math.abs(w - lastWidth) > 2;
      lastWidth = w;

      const { nodes: prevNodes } = stateRef.current;
      if (!widthChanged && prevNodes.length === COUNT) {
        prevNodes.forEach(n => { n.x = Math.min(n.x, w); n.y = Math.min(n.y, h); });
        stateRef.current = { width: w, height: h, nodes: prevNodes, renderNodes: prevNodes.map(n => ({ x: n.x, y: n.y, r: n.r })) };
        return;
      }

      const nodes = Array.from({ length: COUNT }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35, vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.8 + 0.6, phase: Math.random() * Math.PI * 2,
      }));
      // renderNodes mirror nodes 1:1 but hold the cursor-displaced position
      // actually drawn each frame — pre-allocated once so the hover effect
      // never allocates per frame, just mutates in place.
      stateRef.current = { width: w, height: h, nodes, renderNodes: nodes.map(n => ({ x: n.x, y: n.y, r: n.r })) };
    };
    const resize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(applyResize, 150);
    };
    applyResize();
    window.addEventListener('resize', resize);

    // The canvas itself is pointer-events:none (so headline/buttons stay
    // clickable above it), so mouse tracking lives on its parent — the Hero
    // section — and we translate client coords into canvas-local ones here.
    // On mobile `interactive` is false: the network still drifts on its
    // own, but a tap never triggers the repel/synapse reaction (some mobile
    // browsers fire a one-off synthetic "mousemove" on tap, which would
    // otherwise leave a glow stuck at the last tap point with no
    // mousemove/mouseleave afterward to clear it).
    const parent = canvas.parentElement;
    const mouse = mouseRef.current;
    const onMove = (e) => {
      if (!interactiveRef.current) return;
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.target = 1;
    };
    const onLeave = () => { mouse.target = 0; };
    parent?.addEventListener('mousemove', onMove, { passive: true });
    parent?.addEventListener('mouseleave', onLeave, { passive: true });

    const draw = () => {
      const { nodes, renderNodes, width, height } = stateRef.current;
      const rgb = nerveRgbRef.current;
      const rgbEnd = nerveRgbEndRef.current;
      // Nodes/links ease from `rgb` to `rgbEnd` over the bottom ~22% of the
      // canvas — kept flat (t=0) through the whole dark stretch above that
      // so it doesn't tint the network anywhere it's already reading fine.
      const colorAt = (y) => {
        if (!rgbEnd) return rgb;
        const t = Math.max(0, Math.min(1, (y / height - 0.78) / 0.22));
        return t <= 0 ? rgb : lerpRgbStr(rgb, rgbEnd, t);
      };
      // Ease active toward target so the whole effect fades in/out instead
      // of snapping when the cursor enters/leaves.
      mouse.active += (mouse.target - mouse.active) * 0.08;
      const hover = mouse.active > 0.01;

      ctx.clearRect(0, 0, width, height);

      // Drift + boundary bounce — the cursor never touches this, it only
      // bends how a node is *drawn* below, so the simulation itself can
      // never destabilize no matter how the mouse moves.
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.x += n.vx; n.y += n.vy; n.phase += 0.018;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
        n.x = Math.max(0, Math.min(width, n.x));
        n.y = Math.max(0, Math.min(height, n.y));

        const rn = renderNodes[i];
        if (hover) {
          const dx = n.x - mouse.x, dy = n.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < REPEL_R && dist > 0.01) {
            const push = (1 - dist / REPEL_R) * MAX_PUSH * mouse.active;
            rn.x = n.x + (dx / dist) * push;
            rn.y = n.y + (dy / dist) * push;
          } else { rn.x = n.x; rn.y = n.y; }
        } else { rn.x = n.x; rn.y = n.y; }
        rn.r = n.r;
      }

      for (let i = 0; i < renderNodes.length; i++) {
        for (let j = i + 1; j < renderNodes.length; j++) {
          const dx = renderNodes[i].x - renderNodes[j].x, dy = renderNodes[i].y - renderNodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINK_DIST) {
            const alpha = (1 - dist / LINK_DIST) * 0.28;
            const rgbI = colorAt(renderNodes[i].y), rgbJ = colorAt(renderNodes[j].y);
            const rgbMid = rgbI === rgbJ ? rgbI : lerpRgbStr(rgbI, rgbJ, 0.5);
            const g = ctx.createLinearGradient(renderNodes[i].x, renderNodes[i].y, renderNodes[j].x, renderNodes[j].y);
            g.addColorStop(0, `rgba(${rgbI},${alpha})`);
            g.addColorStop(0.5, `rgba(${rgbMid},${alpha * 1.4})`);
            g.addColorStop(1, `rgba(${rgbJ},${alpha})`);
            ctx.beginPath(); ctx.moveTo(renderNodes[i].x, renderNodes[i].y); ctx.lineTo(renderNodes[j].x, renderNodes[j].y);
            ctx.strokeStyle = g; ctx.lineWidth = 0.8; ctx.stroke();
          }
        }
      }

      // Synapse lines + a pulsing cursor-node — the network reaching for
      // the cursor like it just joined, the literal "feel the nerve" beat.
      if (hover) {
        const rgbMouse = colorAt(mouse.y);
        for (let i = 0; i < renderNodes.length; i++) {
          const dx = renderNodes[i].x - mouse.x, dy = renderNodes[i].y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < SYNAPSE_R) {
            const alpha = (1 - dist / SYNAPSE_R) * 0.55 * mouse.active;
            ctx.beginPath();
            ctx.moveTo(renderNodes[i].x, renderNodes[i].y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = `rgba(${colorAt(renderNodes[i].y)},${alpha})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
        const cg = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 34);
        cg.addColorStop(0, `rgba(${rgbMouse},${0.35 * mouse.active})`);
        cg.addColorStop(1, `rgba(${rgbMouse},0)`);
        ctx.beginPath(); ctx.arc(mouse.x, mouse.y, 34, 0, Math.PI * 2);
        ctx.fillStyle = cg; ctx.fill();
        ctx.beginPath(); ctx.arc(mouse.x, mouse.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgbMouse},${0.9 * mouse.active})`; ctx.fill();
      }

      for (let i = 0; i < renderNodes.length; i++) {
        const n = nodes[i], rn = renderNodes[i];
        const pulse = (Math.sin(n.phase) + 1) / 2;
        const r = n.r * (1 + pulse * 0.6);
        const rgbNode = colorAt(rn.y);
        const grd = ctx.createRadialGradient(rn.x, rn.y, 0, rn.x, rn.y, r * 5);
        grd.addColorStop(0, `rgba(${rgbNode},${0.3 * pulse + 0.05})`);
        grd.addColorStop(1, `rgba(${rgbNode},0)`);
        ctx.beginPath(); ctx.arc(rn.x, rn.y, r * 5, 0, Math.PI * 2);
        ctx.fillStyle = grd; ctx.fill();
        ctx.beginPath(); ctx.arc(rn.x, rn.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgbNode},${0.55 + pulse * 0.45})`; ctx.fill();
      }
    };

    // This was the single biggest source of scroll/hover jank on the landing
    // page: an unthrottled 60fps loop that (a) never paused once the user
    // scrolled the Hero out of view — it kept running, and allocating a new
    // canvas gradient per node plus per nearby link pair (100-200+ objects/
    // frame with COUNT=125), for the rest of the session — and (b) kept
    // running in a backgrounded tab too. AmbientField.js and GlobeCore.js
    // elsewhere in this app already pause via IntersectionObserver +
    // visibilitychange; this canvas predates that pattern and never got it.
    // Throttled to ~30fps like AmbientField — slow node drift and an eased
    // cursor-follow read just as smooth well under 60fps, for roughly half
    // the draw/allocation cost.
    const FRAME_MS = 1000 / 30;
    let raf = null;
    let lastDraw = 0;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - lastDraw < FRAME_MS) return;
      lastDraw = now;
      draw();
    };
    const start = () => { if (raf == null) raf = requestAnimationFrame(tick); };
    const stop = () => { if (raf != null) { cancelAnimationFrame(raf); raf = null; } };

    let onScreen = true;
    let tabVisible = document.visibilityState !== 'hidden';
    const syncRunState = () => { if (onScreen && tabVisible) start(); else stop(); };

    const io = new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; syncRunState(); }, { threshold: 0.01 });
    io.observe(canvas);

    const onVisChange = () => { tabVisible = document.visibilityState !== 'hidden'; syncRunState(); };
    document.addEventListener('visibilitychange', onVisChange);

    start();

    return () => {
      stop();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisChange);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', resize);
      parent?.removeEventListener('mousemove', onMove);
      parent?.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // zIndex:1 — this canvas is sized to the Hero+Seam wrapper (see call
  // site), not just the Hero section, so it paints above both their flat
  // backgrounds; heroContentRef carries zIndex:2 to stay above it.
  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }} />;
}

// ── Feature SVG icons ────────────────────────────────────────────────────────
const ICONS = {
  dashboard: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="9" height="9" rx="1.5"/>
      <rect x="13" y="3" width="9" height="5" rx="1.5"/>
      <rect x="13" y="12" width="9" height="9" rx="1.5"/>
      <rect x="2" y="16" width="9" height="5" rx="1.5"/>
    </svg>
  ),
  ai: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.4 7.4L22 12l-7.6 2.6L12 22l-2.4-7.4L2 12l7.6-2.6z"/>
    </svg>
  ),
  quant: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  calendar: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <rect x="7.5" y="13.5" width="1.5" height="1.5" rx="0.5" fill="currentColor" stroke="none"/>
      <rect x="11.25" y="13.5" width="1.5" height="1.5" rx="0.5" fill="currentColor" stroke="none"/>
      <rect x="15" y="13.5" width="1.5" height="1.5" rx="0.5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  globe: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  layers: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
      <polyline points="2 17 12 22 22 17"/>
      <polyline points="2 12 12 17 22 12"/>
    </svg>
  ),
  flow: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 1l4 4-4 4"/>
      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
      <path d="M7 23l-4-4 4-4"/>
      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
    </svg>
  ),
  playbook: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7c-1.8-1.5-4.2-2-7-2v14c2.8 0 5.2.5 7 2 1.8-1.5 4.2-2 7-2V5c-2.8 0-5.2.5-7 2z"/>
      <line x1="12" y1="7" x2="12" y2="21"/>
    </svg>
  ),
  whatsapp: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.896 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  ),
  users: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  mentor: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  ),
  event: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 10l5-3v10l-5-3z"/><rect x="2" y="6" width="13" height="12" rx="2"/>
    </svg>
  ),
  resource: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  ),
  newsletter: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
      <path d="M18 14h-8M15 18h-5M10 6h8v4h-8z"/>
    </svg>
  ),
};

// ── Data ─────────────────────────────────────────────────────────────────────


// The Features grid is commented out (2026-10-23) — we're no longer leading
// with the data-platform story, so the landing page leads with the community
// and mentorship instead. The platform itself still exists behind sign-in and
// the FEATURES data below is kept verbatim so the section can be restored
// without rebuilding it. The `features` nav/anchor links were repointed to
// `#community` at the same time (see LandingNavbar.js).
//
// const FEATURES = [
//   { iconKey: 'dashboard', num: '01', title: 'Daily Market Briefing',  desc: 'What to Watch this week, Risk Radar, and top news stories across India, US, and global markets.' },
//   { iconKey: 'ai',        num: '02', title: 'AI-Powered Insights',    desc: 'AI explains market moves, earnings, and catalysts in plain language.' },
//   // Quant Analysis and FPI Tracker cards removed 2026-09-05 — both pages
//   // disabled pending a data-licensing review (Yahoo Finance / NSDL
//   // respectively). See Stock-AI-Backend/src/app.js and src/cron/worker.js.
//   { iconKey: 'calendar',  num: '03', title: 'Monthly Seasonality',    desc: 'Historical monthly return patterns and statistical data for market research.' },
//   { iconKey: 'globe',     num: '04', title: 'Macro Intelligence',     desc: 'Yields, DXY, commodities — the macro forces that drive sector rotations.' },
//   { iconKey: 'layers',    num: '05', title: 'Sector Analysis',        desc: 'Identify which sectors are leading or lagging across Indian and global markets.' },
//   { iconKey: 'playbook',  num: '06', title: 'Monthly Playbook',       desc: 'A monthly research briefing connecting government policy, sector rotation, and valuation into cross-cutting market themes for your research workflow.' },
// ];

// What lands in the community group. Same bento grid the Features section
// used, so the card glow/tilt handlers below are still doing real work.
const COMMUNITY_PERKS = [
  {
    iconKey: 'newsletter', num: '01', tag: 'Every trading day',
    title: 'The Daily Market Briefing',
    desc: 'My newsletter, posted to the group every market morning: the overnight move explained, the top stories across India, US and global markets, and what to watch before the open.',
  },
  {
    iconKey: 'mentor', num: '02', tag: 'Most popular',
    title: 'Mentorship',
    desc: 'A structured 12-module investing course covering fundamentals, valuation, macro, technicals and strategy, taught the way I actually invest, with direct access to me for questions.',
  },
  {
    iconKey: 'resource', num: '03', tag: 'Free for members',
    title: 'Resources',
    desc: 'Checklists, annual-report walkthroughs, valuation templates and reading lists you can use the same day.',
  },
  {
    iconKey: 'event', num: '04', tag: 'Live',
    title: 'Webinars & meet-ups',
    desc: 'Live webinars, market teardowns and in-person meet-ups, announced in the group first.',
  },
  {
    iconKey: 'users', num: '05', tag: `${COMMUNITY_SIZE}+ investors`,
    title: 'The people',
    desc: 'Ask a question and get an answer, from me and from a room full of investors doing the same work.',
  },
];

// A WhatsApp-style render of a real briefing posted to the group (18 Sep).
// Rendered rather than screenshotted so it stays crisp on retina, reflows on
// mobile, and is selectable text.
const BRIEFING_SAMPLE = {
  date: 'Friday, 18 September 2026',
  lede: 'Nifty 50 and Sensex opened higher yesterday. More hawkish than expected Fed rate hike and domestic liquidity management by the RBI are influencing market sentiment.',
  stories: [
    'PM Modi to inaugurate Semicon India 2026, boosting chip stocks',
    'RBI announces ₹2.25 lakh crore VRRR auction for liquidity absorption',
    "India's gems & jewellery exports grow, signaling demand recovery",
    'US stock futures rise, Fed\u2019s hawkish stance creates caution',
    'Bank of England holds rates at 3.75% despite energy inflation',
  ],
  watch: 'RBI 3-day VRRR Auction (Today): the Reserve Bank will absorb ₹2.25 lakh crore of surplus liquidity, which feeds straight into short-term rates and banking-system liquidity.',
  time: '9:03 AM',
};

// What the mentorship actually covers — a condensed view of the 12 modules on
// /mentorship, grouped so the landing page doesn't just repeat the full list.
// The groupings and the count track the catalogue in
// Stock-AI-Backend/src/routes/mentorship.js, which is what enrolled members
// actually see, mirrored into MODULES_STATIC in pages/Mentorship.js. Keep the
// three in step.
const MENTORSHIP_TRACKS = [
  { num: '01-03', title: 'Foundations',          desc: 'How exchanges work, how prices actually move, and the difference between returns built on earnings and returns built on sentiment.' },
  { num: '04-07', title: 'Analysis & valuation', desc: 'Reading financials for red flags, the macro that drives rotation, picking the right sector, and pricing a business so you never overpay.' },
  { num: '08-09', title: 'Global & technicals',  desc: 'Profiting from global macro themes, plus the chart reading that times a fundamentally sound pick.' },
  { num: '10-12', title: 'Strategy & execution', desc: 'Momentum, QARP and contrarian playbooks, the multibagger checklist, and building your first real portfolio end to end.' },
];

const MENTORSHIP_OUTCOMES = [
  "Read any company's financials with confidence",
  'Filter thousands of stocks down to a real watchlist',
  'Price a business and spot a margin of safety',
  'Build a conviction-weighted portfolio you can hold',
  'Use macro and technicals to time entries and manage risk',
  'Find potential multibaggers before the crowd does',
];

// ── Testimonials ────────────────────────────────────────────────────────────
// Both arrays are empty deliberately: the section renders nothing until real
// material is added, so no invented social proof can ship by accident.
//
// VIDEO_TESTIMONIALS — drop files in public/testimonials/ and reference them
// by their public path. `poster` is the still shown before playback; without
// one the browser shows a black frame, so it's worth exporting a frame.
// A single entry renders as one featured player beside its caption; two or
// more fall into a grid. `aspect` must match the file's real shape or the
// player letterboxes itself. Shape:
//   { src: '/testimonials/name.mp4', poster: '/testimonials/name.jpg',
//     aspect: '4 / 3', name: 'Full Name', role: 'Wipro',
//     quote: 'optional pull quote' }
//
// Source files are transcoded before they land in public/testimonials/:
// phone recordings arrive as HEVC, which Chrome and Firefox will not play, so
// everything here is H.264 + AAC with `-movflags +faststart` so playback can
// begin before the file finishes downloading.
const VIDEO_TESTIMONIALS = [
  {
    src: '/testimonials/sherin-shaji.mp4',
    poster: '/testimonials/sherin-shaji.jpg',
    aspect: '4 / 3',
    name: 'Sherin Shaji',
    role: 'Mentorship member · Wipro',
    quote: 'The way you are explaining the things, that really helped me to understand the markets, especially the fundamental analysis. Before that I couldn’t even understand what the stock is about, and now I can easily do the analysis and all. I understood whatever you taught, like fundamental analysis, macro, finding the right sector and all.',
  },
  {
    src: '/testimonials/bhavesh-m.mp4',
    poster: '/testimonials/bhavesh-m.jpg',
    aspect: '800 / 354',
    name: 'Bhavesh M',
    role: 'Mentorship member',
    quote: 'I’ve seen your story and it sounded more real. I started from zero because I had no idea about stocks or investment. I didn’t have to put in a lot of effort. The videos were concise but really informative, so it made it very easy for me to learn everything about it. The live sessions helped me clear all the doubts and talk about things other than what is recorded. Four, five sessions later I asked you about two, three stocks and you were like, yeah, this is something you were looking into as well.',
  },
];

// Written quotes, shown under the videos. Shape: { quote, name, role }.
const TESTIMONIALS = [];

const TICKER_ITEMS = ['RELIANCE', 'TCS', 'INFY', 'HDFC', 'ICICIBANK', 'AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META', 'WIPRO', 'BAJFINANCE', 'NIFTY 50', 'S&P 500', 'AMZN', 'TSLA', 'HDFCBANK', 'AXISBANK'];

// Forest → Parchment seam gradient. A plain 2-stop linear-gradient between
// #0A1812 and #F4F0E4 desaturates through a muddy grey/white band in the
// middle (interpolating R/G/B independently from a dark cold value to a
// light warm one crosses a low-saturation midpoint). Routing through an
// explicit warm olive/gold midpoint — mixes of the Forest bg and Old Gold
// action color, then Gold and Parchment — keeps the blend "warm dark to
// warm light" the whole way instead. Forest holds through the first third
// (0–32%) before anything lightens; the gold-heaviest stop sits at 48%.
//
// The 0% stop MUST equal C.bg (#0A1812) exactly, not bgDeep (#07100D) — the
// Hero section above (and the Final CTA section this same table drives in
// reverse, below) are flat C.bg. Starting the seam one shade darker than
// the flat panel it touches is what produced the visible seam line: a real
// numeric color step at the boundary, not a rendering artifact.
const SEAM_STOPS = [
  [0,   '#0A1812'], // == C.bg exactly — matches the flat Hero/CTA panel it borders, so the seam starts with zero visible step
  [16,  '#0A1812'], // bg — deep forest, still flat
  [32,  '#152019'], // surfaceHi — muted forest, barely warming
  [48,  '#635624'], // Forest/Gold mix — warm olive atmosphere
  [64,  '#DEC997'], // Gold/Parchment mix — warm gold-cream
  [80,  '#EDE3CA'], // soft warm cream, near surfaceHi (light)
  [100, '#F4F0E4'], // == CL.bg exactly — matches the flat Parchment panel it borders
];
const SEAM_STOPS_REVERSE = SEAM_STOPS.map(([p, c]) => [100 - p, c]).reverse();

/* Kept for the parked Pricing section below; the live plans table is in pages/Pricing.js.

const PRICING = [
  {
    name: 'Free', price: '₹0', period: '/month', icon: 'spark',
    desc: 'Get started with market data and research tools',
    // 'Quant Indicators (basic)' and 'FPI Tracker' removed 2026-09-05 — both pages disabled, see FEATURES comment above
    included:    ['Full Market News Briefing (India, US & Global)', 'Stock search & detail pages', 'Monthly Seasonality', 'Macro Intelligence'],
    notIncluded: ['Monthly Playbook', 'Portfolio Doctor', 'Filter-based Screener (220 EMA)', 'Learning content & insights', 'AI Insights'],
    cta: 'Start Free', highlight: false,
  },
  {
    name: 'Pro', price: '₹499', period: '/month', icon: 'crown',
    desc: 'Full access to every research tool on the platform',
    included:    ['Everything in Free', 'Monthly Playbook', 'Filter-based Screener (220 EMA)', 'AI Insights', 'Learning content & insights', 'Portfolio Doctor'],
    notIncluded: [],
    cta: 'Join Free, Get Pro First', highlight: true, badge: 'Coming Soon',
  },
];

const PLAN_ICONS = {
  spark: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  crown: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 18h20l-2-9-5 4-3-7-3 7-5-4z" />
      <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
  ),
};

*/

const STEPS = [
  { num: '01', title: 'Join the community',  desc: `Tap the WhatsApp link and you're in with ${COMMUNITY_SIZE}+ other investors. No form, no fee.` },
  { num: '02', title: 'Read the briefing',   desc: 'The daily market briefing lands in the group every trading morning. Start there.' },
  { num: '03', title: 'Go deep with mentorship', desc: '12 modules, resources and live events when you want to go past the headlines.' },
];

const FAQS = [
  {
    q: 'What is the MarketNerve community?',
    a: `A WhatsApp community of ${COMMUNITY_SIZE}+ investors where I post the daily market briefing, share resources, run the mentorship programme, and announce webinars and in-person meet-ups. Joining is free. Tap the WhatsApp link anywhere on this page and you're in.`,
  },
  {
    q: 'What is the daily briefing, and what does it cost?',
    a: "It's my newsletter, posted to the community group every trading morning: the overnight move explained, the top stories across India, the US and global markets, and what to watch before the open. Each edition is generated and then reviewed by me before it goes out. It's free for everyone in the group, with no subscription and no email signup.",
  },
  {
    q: 'What does the mentorship include?',
    a: '12 structured modules covering market fundamentals, how prices move, reading financials, macro, sector selection, valuation, global and thematic investing, technicals, strategy and finding multibaggers, plus the full slide decks, community access and direct answers from me. It is a one-time ₹2,199 payment with lifetime access.',
  },
  {
    q: 'Are there events?',
    a: 'Yes. Live webinars, market teardowns and in-person meet-ups. Everything is announced in the community group first, so joining is how you hear about them.',
  },
  {
    q: 'Is MarketNerve financial advice?',
    a: 'No. MarketNerve provides market data, quantitative indicators, and AI-generated analysis for informational and educational purposes only. Nothing on the platform is financial, investment, legal, or tax advice. Always consult a qualified financial advisor before making investment decisions.',
  },
  {
    q: 'Which markets does MarketNerve cover?',
    a: 'Indian equities (NSE & BSE) and major global markets (US, and broader international coverage), including sector data and macro indicators.',
  },
  {
    q: 'Is there really a free plan?',
    a: 'Yes. The Free plan includes the full market news briefing, stock search, monthly seasonality, and macro intelligence. No credit card required.',
  },
  {
    q: 'How is this different from a standard stock screener?',
    a: 'Most screeners stop at raw numbers. MarketNerve connects macro data, sector rotation, and AI-generated explanations into a single research workflow, so you understand the "why" behind the "what."',
  },
  {
    q: 'When does the Pro plan launch?',
    a: "Pro is in final testing. Sign up free today and you'll be notified the moment it opens. Early sign-ups get first access.",
  },
];

function FAQItem({ item, isOpen, onToggle, C, nerveRgb }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '1rem', padding: '1.375rem 0.25rem', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', fontFamily: LP.heading,
        }}
      >
        <span style={{ fontSize: '0.95rem', fontWeight: 700, color: C.text, letterSpacing: '-0.01em' }}>
          {item.q}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          style={{
            flexShrink: 0, width: '28px', height: '28px', borderRadius: '50%',
            background: `rgba(${nerveRgb},0.1)`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.action,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <p style={{ margin: '0 0 1.375rem', color: C.text, opacity: 0.75, fontSize: '0.875rem', lineHeight: 1.75, fontWeight: 300, fontFamily: LP.body, maxWidth: '640px' }}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── WhatsApp briefing mock ───────────────────────────────────────────────────
// A render of a real briefing as it lands in the community group, rather than
// a screenshot: stays crisp on retina, reflows on mobile, and the text is
// selectable (and indexable). Colors are WhatsApp's own, fixed rather than
// themed — the whole point is that it reads as a genuine artifact from the
// group, so it shouldn't pick up the Parchment palette around it.
const WA = {
  chatBg:   '#E4DDD4',   // the doodled chat canvas
  bubble:   '#D9FDD3',   // outgoing (admin) bubble
  text:     '#111B21',
  meta:     'rgba(17,27,33,0.45)',
  tick:     '#53BDEB',
  headerBg: '#F0F2F5',
};

function BriefingMock({ isMobile }) {
  return (
    <div style={{
      position: 'relative',
      borderRadius: '1.25rem',
      overflow: 'hidden',
      border: '1px solid rgba(10,24,18,0.14)',
      boxShadow: '0 24px 64px rgba(10,24,18,0.18), 0 2px 8px rgba(10,24,18,0.08)',
      background: WA.chatBg,
      maxWidth: '460px',
      width: '100%',
      margin: '0 auto',
    }}>
      {/* Chat header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.7rem',
        padding: '0.75rem 1rem',
        background: WA.headerBg,
        borderBottom: '1px solid rgba(10,24,18,0.08)',
      }}>
        <img src="/logo.png" alt="" aria-hidden="true" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: WA.text, fontFamily: LP.heading, letterSpacing: '-0.01em' }}>
            MarketNerve
          </div>
          <div style={{ fontSize: '0.68rem', color: WA.meta, fontFamily: LP.heading, fontWeight: 500 }}>
            {COMMUNITY_SIZE}+ members · Community
          </div>
        </div>
        <div style={{ marginLeft: 'auto', color: '#25D366', display: 'flex', alignItems: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12 0C5.373 0 0 5.373 0 12c0 2.136.558 4.142 1.535 5.88L0 24l6.335-1.652A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0"/></svg>
        </div>
      </div>

      {/* Date divider */}
      <div style={{ textAlign: 'center', padding: '0.9rem 0 0.2rem' }}>
        <span style={{
          display: 'inline-block', background: 'rgba(255,255,255,0.85)',
          color: WA.meta, fontSize: '0.62rem', fontWeight: 600,
          padding: '0.25rem 0.7rem', borderRadius: '0.45rem',
          fontFamily: LP.heading, letterSpacing: '0.03em', textTransform: 'uppercase',
        }}>
          Fri, 18 Sep
        </span>
      </div>

      {/* The message bubble */}
      <div style={{ padding: isMobile ? '0.5rem 0.75rem 1.5rem' : '0.5rem 1rem 1.75rem' }}>
        <div style={{
          position: 'relative',
          background: WA.bubble,
          borderRadius: '0.6rem 0.6rem 0.1rem 0.6rem',
          padding: isMobile ? '0.75rem 0.85rem 0.5rem' : '0.85rem 1rem 0.55rem',
          boxShadow: '0 1px 1px rgba(10,24,18,0.12)',
          fontFamily: LP.body,
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0A6E4A', fontFamily: LP.heading, marginBottom: '0.5rem' }}>
            You <span style={{ fontWeight: 500, color: WA.meta }}>· Community admin</span>
          </div>

          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: WA.text, fontFamily: LP.heading, lineHeight: 1.35 }}>
            MarketNerve · Daily Market Briefing
          </div>
          <div style={{ fontSize: '0.74rem', color: WA.meta, fontFamily: LP.heading, fontWeight: 500, marginBottom: '0.65rem' }}>
            {BRIEFING_SAMPLE.date}
          </div>

          <p style={{ margin: '0 0 0.85rem', fontSize: '0.78rem', lineHeight: 1.6, color: WA.text, fontWeight: 400 }}>
            {BRIEFING_SAMPLE.lede}
          </p>

          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: WA.text, fontFamily: LP.heading, marginBottom: '0.4rem' }}>
            Top Stories
          </div>
          <ul style={{ margin: '0 0 0.9rem', paddingLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {BRIEFING_SAMPLE.stories.map((st) => (
              <li key={st} style={{ fontSize: '0.745rem', lineHeight: 1.5, color: WA.text, fontWeight: 400 }}>{st}</li>
            ))}
          </ul>

          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: WA.text, fontFamily: LP.heading, marginBottom: '0.4rem' }}>
            What to Watch
          </div>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.745rem', lineHeight: 1.5, color: WA.text, fontWeight: 400 }}>
            {BRIEFING_SAMPLE.watch}
          </p>

          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem' }}>
            <span style={{ fontSize: '0.7rem', fontStyle: 'italic', color: WA.meta }}>Sent via MarketNerve</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.62rem', color: WA.meta, fontFamily: LP.heading, whiteSpace: 'nowrap' }}>
              {BRIEFING_SAMPLE.time}
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke={WA.tick} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="1.5 9.5 4.5 12.5 10 6" /><polyline points="7 9.5 9 11.5 14.5 5" />
              </svg>
            </span>
          </div>

          {/* Reaction chip */}
          <div style={{
            position: 'absolute', bottom: '-13px', right: '0.9rem',
            background: '#fff', borderRadius: '2rem',
            padding: '0.1rem 0.45rem', fontSize: '0.7rem',
            boxShadow: '0 1px 4px rgba(10,24,18,0.2)',
            display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
          }}>
            <span aria-hidden="true">❤️</span>
            <span aria-hidden="true">🙏</span>
            <span style={{ fontSize: '0.6rem', color: WA.meta, fontFamily: LP.heading, fontWeight: 600 }}>12</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { user } = useAuth();
  useTheme(); // keep context subscription for potential future use
  // The landing page doesn't follow the app's dark/light toggle — it's a
  // marketing page, pinned Parchment-light by default with Forest used
  // strategically for the Hero, ticker band, final CTA and footer (brand
  // system: Forest = authority, Parchment = the editorial canvas, Gold = signal).
  const { colors: CD, nerveRgb: nerveRgbD } = JADE_DARK;
  const { colors: CL, nerveRgb: nerveRgbL } = JADE_LIGHT;
  const { isMobile, isMd, isDesktop } = useBreakpoints();
  const navigate = useNavigate();
  const location = useLocation();

  // Sign-in is a popup card over this page, not a separate /login route.
  // A protected route that redirects here sets state.openLogin to pop it
  // open automatically. Clearing the flag via replace means this effect
  // naturally goes quiet after the first run — no mount-only trick needed.
  const [showLogin, setShowLogin] = useState(!!location.state?.openLogin);
  useEffect(() => {
    if (location.state?.openLogin) {
      setShowLogin(true);
      // Clear the flag so a later back/forward nav doesn't re-open it.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [hoveredStep, setHoveredStep]   = useState(null);
  // const [hoveredPlan, setHoveredPlan]   = useState(null); // Pricing section, parked below
  const [openFaq, setOpenFaq]           = useState(0);
  const [prefersReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Section heading reveal refs
  const featHeadRef  = useReveal('lp-reveal');
  const stepsHeadRef = useReveal('lp-reveal');
  // const priceHeadRef = useReveal('lp-reveal'); // Pricing section, parked below
  const ctaRef       = useReveal('lp-reveal-scale', 0.2);

  // GSAP ScrollTrigger stagger reveal refs for card groups (see useGsapStagger)
  const featGridRef  = useGsapStagger('.gsap-card', { stagger: 0.08 });
  const stepsGridRef = useGsapStagger('.gsap-card', { stagger: 0.12, y: 24 });
  // const priceGridRef = useGsapStagger('.gsap-card', { stagger: 0.14, y: 36 }); // Pricing section, parked below

  // Count-up for the community-size stat
  const [counterRef, counterVal] = useCountUp(COMMUNITY_SIZE, 1800);

  // Mouse-tracking glow + 3D tilt for feature cards (no re-render, direct DOM
  // write) — the radial glow tracks the cursor via CSS custom properties
  // while GSAP quickTo owns a subtle perspective tilt + lift, React Bits
  // "TiltedCard"-style (https://reactbits.dev/components/tilted-card).
  const cardRefs = useRef([]);
  const cardTweensRef = useRef([]);
  const getCardTweens = (el, i) => {
    if (!cardTweensRef.current[i]) {
      gsap.set(el, { transformPerspective: 800, transformOrigin: '50% 50%' });
      cardTweensRef.current[i] = {
        rx: gsap.quickTo(el, 'rotateX', { duration: 0.5, ease: 'power3.out' }),
        ry: gsap.quickTo(el, 'rotateY', { duration: 0.5, ease: 'power3.out' }),
        ty: gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3.out' }),
      };
    }
    return cardTweensRef.current[i];
  };
  const handleCardMouseMove = useCallback((e, i) => {
    const el = cardRefs.current[i];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
    if (prefersReducedMotion) return;
    const { rx, ry } = getCardTweens(el, i);
    rx((0.5 - py) * 6);
    ry((px - 0.5) * 6);
  }, [prefersReducedMotion]);
  const handleCardMouseLeave = useCallback((i) => {
    setHoveredFeature(null);
    const el = cardRefs.current[i];
    if (!el || prefersReducedMotion) return;
    const { rx, ry, ty } = getCardTweens(el, i);
    rx(0); ry(0); ty(0);
  }, [prefersReducedMotion]);
  const handleCardMouseEnter = useCallback((i) => {
    setHoveredFeature(i);
    const el = cardRefs.current[i];
    if (!el || prefersReducedMotion) return;
    getCardTweens(el, i).ty(-6);
  }, [prefersReducedMotion]);

  // GSAP scroll-scrub parallax for the Hero: the grid drifts down slower
  // than scroll while the headline/content drifts up a touch faster,
  // giving the section depth as it scrolls away under the ticker band.
  const heroSectionRef = useRef(null);
  const heroGridRef    = useRef(null);
  const heroContentRef = useRef(null);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const ctx = gsap.context(() => {
      if (heroGridRef.current) {
        gsap.to(heroGridRef.current, {
          yPercent: 18, ease: 'none',
          scrollTrigger: { trigger: heroSectionRef.current, start: 'top top', end: 'bottom top', scrub: true },
        });
      }
      if (heroContentRef.current) {
        gsap.to(heroContentRef.current, {
          yPercent: -12, opacity: 0.4, ease: 'none',
          scrollTrigger: { trigger: heroSectionRef.current, start: 'top top', end: 'bottom top', scrub: true },
        });
      }
    }, heroSectionRef);
    return () => ctx.revert();
  }, [prefersReducedMotion]);

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  // Expose nerveRgb as a CSS custom property for mouse-glow gradients
  useEffect(() => {
    document.documentElement.style.setProperty('--nerve-rgb', nerveRgbD);
  }, [nerveRgbD]);

  // Header/Hero/ticker/final-CTA/footer stay Forest-dark (C/nerveRgb/isDark);
  // the Ticker-band-through-FAQ content region below uses CL/nerveRgbL (Parchment).
  const C = CD;
  const nerveRgb = nerveRgbD;
  const isDark = true;

  // Light-section equivalent of the alternating card-band gradient used by
  // the Features and Pricing sections.
  const sectionAltLight = `linear-gradient(180deg, ${CL.bg} 0%, ${CL.surfaceHi} 50%, ${CL.bg} 100%)`;

  return (
    <div style={{ background: CL.bg, color: CL.text, minHeight: '100vh', fontFamily: LP.body, transition: 'background 0.4s, color 0.4s', overflowX: 'hidden' }}>

      {/* ── NAV ── */}
      <LandingNavbar onSignIn={() => setShowLogin(true)} />

      {/* ── HERO + SEAM WRAPPER ── */}
      {/* The network canvas AND the grid-line pattern used to live inside
          the Hero section alone, so both hard-stopped at Hero's own bottom
          edge — anything past that (the seam's dark stretch before it
          starts warming) was plain flat C.bg with neither, no matter how
          the old Hero-local vignette was tuned. Both now live at this
          wrapper level, sized to Hero + the seam together, so the network
          and the grid genuinely extend through the whole dark stretch
          instead of vanishing at a boundary the seam doesn't share.
          zIndex keeps the canvas above both sections' flat backgrounds but
          below Hero's own text (see heroContentRef's zIndex below); the
          grid stays below the canvas, at the default stack level. */}
      <div style={{ position: 'relative' }}>
        <div ref={heroGridRef} style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 75% 60% at 50% 30%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 75% 60% at 50% 30%, black 30%, transparent 100%)',
        }} />
      {/* ── HERO ── */}
      {/* Needs its own solid Forest background — the page root is Parchment
          now. */}
      <section ref={heroSectionRef} style={{
        textAlign: 'center', padding: isMobile ? '5rem 1.25rem 3.5rem' : '7rem 2rem 5rem',
        position: 'relative', overflow: 'hidden', background: C.bg,
        minHeight: '88vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div className="animate-orb" style={{
          position: 'absolute', top: '18%', left: '50%', transform: 'translateX(-50%)',
          width: '700px', height: '420px',
          background: `radial-gradient(ellipse, rgba(${nerveRgb},0.12) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <div ref={heroContentRef} style={{ position: 'relative', zIndex: 2, maxWidth: '880px', margin: '0 auto' }}>
          {/* Live badge */}
          {/* <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            background: `rgba(${nerveRgb},0.08)`, border: `1px solid ${C.borderHi}`,
            borderRadius: '2rem', padding: '0.45rem 1.25rem', marginBottom: '2.25rem',
            fontSize: '0.72rem', color: C.textMuted, fontFamily: LP.heading,
            fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            animation: prefersReducedMotion ? 'none' : 'fadeInUp 0.5s ease forwards',
          }}>
            <span style={{ width: '7px', height: '7px', background: C.action, borderRadius: '50%', boxShadow: `0 0 10px ${C.action}`, flexShrink: 0 }} className={prefersReducedMotion ? '' : 'animate-pulse-glow'} />
            AI-powered market intelligence · Now live
          </div> */}

          {/* Offer badge — the two things the page is actually selling */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
            justifyContent: 'center',
            background: `rgba(${nerveRgb},0.08)`, border: `1px solid ${C.borderHi}`,
            borderRadius: '2rem', padding: '0.45rem 1.25rem', marginBottom: '2rem',
            fontSize: '0.7rem', color: C.text, fontFamily: LP.heading,
            fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            animation: prefersReducedMotion ? 'none' : 'fadeInUp 0.5s ease forwards',
          }}>
            <span style={{ color: C.action }}>12-Module Mentorship</span>
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: C.borderHi, flexShrink: 0 }} />
            <span style={{ color: C.textMuted }}>{COMMUNITY_SIZE}+ Investor Community</span>
          </div>

          {/* Hero headline — word-by-word reveal */}
          <h1 style={{
            fontSize: 'clamp(3rem, 7.5vw, 5.75rem)',
            fontWeight: 800, lineHeight: 1.03,
            marginBottom: '1.75rem', letterSpacing: '-0.035em',
            color: C.text, fontFamily: LP.heading,
          }}>
            <span style={{ display: 'block' }}>
              {['Know', 'The', 'Market.'].map((word, i) => (
                <span key={word} style={{
                  display: 'inline-block',
                  animation: prefersReducedMotion ? 'none' : `wordIn 0.55s cubic-bezier(0.16,1,0.3,1) ${0.08 + i * 0.09}s both`,
                  marginRight: '0.25em',
                }}>{word}</span>
              ))}
            </span>
            <span style={{
              background: `linear-gradient(135deg, ${C.actionHi} 0%, ${C.action} 45%, ${C.actionLo} 100%)`,
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              display: 'block',
              animation: prefersReducedMotion ? 'none' : 'wordIn 0.6s cubic-bezier(0.16,1,0.3,1) 0.36s both, shimmer 4s linear 0.36s infinite',
            }}>
              Feel The Nerve.
            </span>
          </h1>

          <p style={{
            fontSize: '1.1rem', color: C.text,
            maxWidth: '520px', margin: '0 auto 3.25rem',
            lineHeight: 1.7, fontWeight: 300, fontFamily: LP.body,
            animation: prefersReducedMotion ? 'none' : 'fadeInUp 0.65s ease 0.6s both',
            opacity: 0.85,
          }}>
            Learn to invest through a structured 12-module mentorship, and join a
            {' '}{COMMUNITY_SIZE}+ strong community on WhatsApp for the daily market briefing,
            resources and live events.
          </p>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', animation: prefersReducedMotion ? 'none' : 'fadeInUp 0.65s ease 0.72s both' }}>
            <Magnet padding={80} strength={5}>
              <Link to="/mentorship?source=landing" style={{
                background: C.action, color: isDark ? '#fff' : C.bg,
                padding: '1rem 2.5rem', borderRadius: '0.75rem',
                border: 'none', cursor: 'pointer', fontWeight: 700,
                fontSize: '1rem', fontFamily: LP.heading, letterSpacing: '-0.01em',
                textDecoration: 'none',
                boxShadow: `0 0 40px rgba(${nerveRgb},0.38)`, transition: 'all 0.22s',
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = C.actionHi; e.currentTarget.style.boxShadow = `0 0 60px rgba(${nerveRgb},0.52)`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.action; e.currentTarget.style.boxShadow = `0 0 40px rgba(${nerveRgb},0.38)`; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Explore the Mentorship
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </Magnet>
            <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={{
              background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.4)',
              color: '#4AE08A', padding: '1rem 2.25rem', borderRadius: '0.75rem',
              textDecoration: 'none', fontWeight: 700, fontFamily: LP.heading, fontSize: '1rem',
              display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(37,211,102,0.75)'; e.currentTarget.style.background = 'rgba(37,211,102,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(37,211,102,0.4)'; e.currentTarget.style.background = 'rgba(37,211,102,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              {ICONS.whatsapp}
              Join the Community
            </a>
          </div>

          {/* Platform sign-in — kept available, but it's no longer what the
              hero is selling, so it sits under the two real CTAs. */}
          <div style={{ marginTop: '1.25rem', animation: prefersReducedMotion ? 'none' : 'fadeInUp 0.65s ease 0.8s both' }}>
            <button onClick={() => setShowLogin(true)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
              color: C.textMuted, fontSize: '0.82rem', fontFamily: LP.heading, fontWeight: 600,
              textDecoration: 'underline', textUnderlineOffset: '4px', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = C.text}
              onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
              Or sign in to the free research platform
            </button>
          </div>

          {/* Hero stats */}
          <div style={{
            display: 'flex', gap: '0', justifyContent: 'center', flexWrap: 'wrap',
            marginTop: '5rem', borderTop: `1px solid ${C.border}`, paddingTop: '2.75rem',
            animation: prefersReducedMotion ? 'none' : 'fadeInUp 0.65s ease 0.88s both',
          }}>
            {[
              { ref: null,        val: '12 Modules',                      label: 'Mentorship' },
              { ref: counterRef, val: `${counterVal.toLocaleString()}+`, label: 'In the Community' },
              { ref: null,        val: 'Daily',                           label: 'Market Briefing' },
              { ref: null,        val: 'Live',                            label: 'Webinars & Meet-ups' },
            ].map(({ ref: statRef, val, label }, i, arr) => (
              <div key={label} ref={statRef} style={{ textAlign: 'center', padding: isMobile ? '0 1.125rem' : '0 2.5rem', borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: C.text, fontFamily: LP.data }}>{val}</div>
                <div style={{ fontSize: '0.68rem', color: C.actionLo, marginTop: '0.35rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: LP.heading }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEAM: Hero → Ticker ── */}
      {/* Forest dissolves into Parchment through a multi-stop gradient, not a
          naive 2-color blend — a straight #0A1812→#F4F0E4 interpolation
          desaturates through a muddy grey/white band in the middle. The
          extra stops route the blend through a warm olive/gold midpoint
          instead, so it stays "warm dark → warm light" the whole way. Forest
          holds through the first third before anything lightens. A faint
          atmospheric gold glow (not a bright bloom) sits at the warm
          midpoint — no dot/starfield texture here, it read as a separate
          layer sitting on top of the gradient instead of part of it. */}
      <div aria-hidden="true" style={{
        position: 'relative', overflow: 'hidden',
        height: isMobile ? '200px' : '320px',
        background: `linear-gradient(to bottom, ${SEAM_STOPS.map(([p, c]) => `${c} ${p}%`).join(', ')})`,
      }}>
        <div style={{
          position: 'absolute', top: '42%', left: '50%', transform: 'translateX(-50%)',
          width: 'min(1500px, 170vw)', height: '58%',
          background: `radial-gradient(ellipse 60% 55% at 50% 50%, rgba(${nerveRgb},0.08) 0%, transparent 45%)`,
          pointerEvents: 'none',
        }} />
      </div>

        {/* Ambient drift stays on mobile — the network still lives back
            there — but `interactive=false` means a tap never triggers the
            repel/synapse reaction, which only makes sense for a cursor. Sized
            to this wrapper (Hero + seam), not just Hero — see comment above.
            nerveRgbEnd='122,90,16' is CL.actionLo (#7A5A10) as an rgb string —
            the palette's deep-gold token already tuned for Parchment, so
            nodes stay legible instead of washing out once the seam turns
            light beneath them. */}
        <NervesCanvas nerveRgb={nerveRgb} nerveRgbEnd="122,90,16" interactive={!isMobile} />
      </div>

      {/* ── TICKER BAND ── */}
      <div style={{
        borderTop: `1px solid ${CL.border}`, borderBottom: `1px solid ${CL.border}`,
        overflow: 'hidden', padding: '0.875rem 0',
        background: `rgba(${nerveRgbL},0.04)`,
      }}>
        <div className="ticker-track" style={{ display: 'flex', gap: '0', alignItems: 'center', whiteSpace: 'nowrap', width: 'max-content' }}>
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
            <React.Fragment key={i}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                padding: '0 1.5rem',
                fontSize: '0.68rem', fontFamily: LP.data, fontWeight: 700,
                letterSpacing: '0.12em',
                color: i % 4 === 0 ? CL.action : i % 4 === 2 ? CL.actionLo : CL.textDim,
              }}>
                <span style={{
                  width: '4px', height: '4px', borderRadius: '50%',
                  background: i % 3 === 0 ? CL.action : CL.border,
                  display: 'inline-block', flexShrink: 0,
                }} />
                {t}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── FEATURES (commented out 2026-10-23) ── */}
      {/* The feature grid told the data-platform story. We're leading with the
          community and mentorship instead, so the whole section is parked
          here rather than deleted — restoring it means un-commenting this JSX
          and the FEATURES array near the top of the file. The card
          glow/tilt handlers it used (cardRefs, handleCardMouse*,
          hoveredFeature, featGridRef) are now driving the Community perk
          cards below, so nothing is orphaned while this sits idle. */}

      {/* ── COMMUNITY ── */}
      <section id="community" style={{ padding: isMobile ? '4rem 1rem' : '6rem 2rem', position: 'relative', background: sectionAltLight }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '120px', background: `linear-gradient(to bottom, ${CL.bg}, transparent)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '120px', background: `linear-gradient(to top, ${CL.bg}, transparent)`, pointerEvents: 'none' }} />
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `radial-gradient(${CL.border} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
          opacity: 0.6,
        }} />

        <div style={{ maxWidth: '1160px', margin: '0 auto', position: 'relative' }}>
          <div ref={featHeadRef} style={{ textAlign: 'center', marginBottom: isMobile ? '2.75rem' : '4rem' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.35)',
              borderRadius: '2rem', padding: '0.4rem 1.125rem', marginBottom: '1.5rem',
              fontSize: '0.68rem', fontFamily: LP.heading, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: '#1E9E54',
            }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#25D366', boxShadow: '0 0 10px rgba(37,211,102,0.8)', flexShrink: 0 }} className={prefersReducedMotion ? '' : 'animate-pulse-glow'} />
              The Community
            </div>
            <h2 style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
              fontWeight: 800, marginBottom: '1rem',
              letterSpacing: '-0.03em', fontFamily: LP.heading, color: CL.text,
              lineHeight: 1.1,
            }}>
              {COMMUNITY_SIZE}+ investors.<br />
              <span style={{ color: CL.action }}>One WhatsApp group.</span>
            </h2>
            <p style={{ color: CL.text, fontSize: '0.98rem', lineHeight: 1.7, fontWeight: 300, fontFamily: LP.body, maxWidth: '560px', margin: '0 auto', opacity: 0.78 }}>
              My daily market briefing, the mentorship programme, resources and live
              events, all run out of one community you can join in a tap.
            </p>
          </div>

          {/* Newsletter proof: a real briefing, rendered as it lands in the group */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr',
            gap: isMobile ? '2.5rem' : '3.5rem',
            alignItems: 'center',
            marginBottom: isMobile ? '3rem' : '4.5rem',
          }}>
            <BriefingMock isMobile={isMobile} />

            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                fontSize: '0.66rem', fontFamily: LP.heading, fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase', color: CL.action,
                marginBottom: '1rem',
              }}>
                The Newsletter
              </div>
              <h3 style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', fontWeight: 800,
                letterSpacing: '-0.03em', fontFamily: LP.heading, color: CL.text,
                lineHeight: 1.15, margin: '0 0 1rem',
              }}>
                The market, explained before the open
              </h3>
              <p style={{ color: CL.text, fontSize: '0.95rem', lineHeight: 1.75, fontWeight: 300, fontFamily: LP.body, margin: '0 0 1.75rem', opacity: 0.8 }}>
                Every trading morning the briefing is generated, reviewed by me, and
                posted straight to the group: the overnight move explained, the stories
                that actually matter across India, the US and global markets, and the one
                or two things worth watching before the bell. No noise, no tips, no
                charts you have to decode.
              </p>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {[
                  'Posted every trading day, around 9 AM IST',
                  'India, US and global markets in one read',
                  'Generated each morning, then personally reviewed before it goes out',
                  'Free for everyone in the community',
                ].map((line) => (
                  <li key={line} style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '0.65rem' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#25D366" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '0.2rem' }}><polyline points="20 6 9 17 4 12" /></svg>
                    <span style={{ color: CL.text, opacity: 0.88, lineHeight: 1.55, fontWeight: 400, fontFamily: LP.body }}>{line}</span>
                  </li>
                ))}
              </ul>

              <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.65rem',
                background: '#25D366', color: '#07251A',
                padding: '0.95rem 1.9rem', borderRadius: '0.75rem',
                textDecoration: 'none', fontWeight: 700, fontFamily: LP.heading, fontSize: '0.95rem',
                boxShadow: '0 8px 28px rgba(37,211,102,0.35)', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1FBF5C'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(37,211,102,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#25D366'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(37,211,102,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                {ICONS.whatsapp}
                Get the briefing free
              </a>
            </div>
          </div>

          {/* Perk cards — same bento grid, stagger and mouse-glow the Features
              section used, now describing what's inside the community. */}
          <div ref={featGridRef} style={{
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'repeat(12, 1fr)' : isMd ? 'repeat(2, 1fr)' : '1fr',
            gap: '1.25rem',
          }}>
            {COMMUNITY_PERKS.map((f, i) => {
              const gridArea = isDesktop ? [
                { gridColumn: '1 / 7',  gridRow: '1 / 2' },
                { gridColumn: '7 / 13', gridRow: '1 / 2' },
                { gridColumn: '1 / 5',  gridRow: '2 / 3' },
                { gridColumn: '5 / 9',  gridRow: '2 / 3' },
                { gridColumn: '9 / 13', gridRow: '2 / 3' },
              ][i] : {};

              // Mentorship is the card we want people to land on — it gets the
              // gold treatment and routes to /mentorship instead of the group.
              const isLead    = f.iconKey === 'mentor';
              const isHovered = hoveredFeature === i;
              const goTo      = () => { if (isLead) navigate('/mentorship?source=landing'); else window.open(COMMUNITY_URL, '_blank', 'noopener,noreferrer'); };

              return (
                <div
                  key={f.title}
                  ref={el => cardRefs.current[i] = el}
                  className="feature-card gsap-card"
                  role="button" tabIndex={0}
                  style={{
                    ...gridArea,
                    background: isLead
                      ? `linear-gradient(145deg, ${CL.surface} 0%, ${CL.surfaceHi} 100%)`
                      : (isHovered ? CL.surfaceHi : CL.surface),
                    border: isLead ? `1.5px solid ${CL.action}` : `1px solid ${isHovered ? CL.borderHi : CL.border}`,
                    borderRadius: '1.25rem',
                    padding: '2.25rem 2rem',
                    position: 'relative', overflow: 'hidden',
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    boxShadow: isLead
                      ? `0 0 48px rgba(${nerveRgbL},0.18), 0 16px 48px rgba(0,0,0,0.16)`
                      : (isHovered
                        ? `0 16px 48px rgba(0,0,0,0.2), 0 0 0 1px ${CL.borderHi}`
                        : `0 2px 12px rgba(0,0,0,0.08)`),
                    // Entrance (opacity/transform) is driven by GSAP (useGsapStagger);
                    // only the hover-state properties need a CSS transition.
                    transitionProperty: 'background, border-color, box-shadow',
                    transitionDuration: '0.22s',
                    transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
                  }}
                  onMouseMove={e => handleCardMouseMove(e, i)}
                  onMouseEnter={() => handleCardMouseEnter(i)}
                  onMouseLeave={() => handleCardMouseLeave(i)}
                  onClick={goTo}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(); } }}
                >
                  {/* Top accent line */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                    background: (isHovered || isLead) ? `linear-gradient(90deg, transparent, ${CL.action}, transparent)` : 'transparent',
                    transition: 'background 0.3s',
                  }} />

                  {/* Tag pill (replaces the old bare card number) */}
                  <div style={{
                    position: 'absolute', top: '1.25rem', right: '1.5rem',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0.22rem 0.6rem', borderRadius: '2rem',
                    background: isLead ? CL.action : (isHovered ? `rgba(${nerveRgbL},0.14)` : `rgba(${nerveRgbL},0.07)`),
                    border: `1px solid ${isLead ? CL.action : (isHovered ? CL.borderHi : CL.border)}`,
                    fontFamily: LP.heading, fontSize: '0.6rem', fontWeight: 700,
                    color: isLead ? CL.bg : CL.action, letterSpacing: '0.06em', textTransform: 'uppercase',
                    transition: 'background 0.22s, border-color 0.22s',
                  }}>
                    {f.tag}
                  </div>

                  {/* Mouse-tracking radial glow */}
                  <div style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: 'inherit',
                    background: isHovered
                      ? `radial-gradient(350px circle at var(--mx, 50%) var(--my, 50%), rgba(${nerveRgbL},0.1), transparent 65%)`
                      : 'transparent',
                    transition: 'background 0.2s',
                  }} />

                  {/* Icon */}
                  <div style={{
                    width: '50px', height: '50px', flexShrink: 0,
                    borderRadius: '14px',
                    background: `rgba(${nerveRgbL},0.1)`,
                    border: `1px solid rgba(${nerveRgbL},0.2)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: CL.action,
                    marginBottom: '1.25rem',
                    boxShadow: (isHovered || isLead) ? `0 0 24px rgba(${nerveRgbL},0.25)` : 'none',
                    transition: 'box-shadow 0.22s',
                  }}>
                    {ICONS[f.iconKey]}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.05rem',
                      color: CL.text, fontFamily: LP.heading, letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                    }}>
                      {f.title}
                    </h3>
                    <p style={{
                      color: CL.text, fontSize: '0.88rem',
                      lineHeight: 1.65, fontWeight: 300, fontFamily: LP.body,
                      margin: 0, opacity: 0.8,
                    }}>
                      {f.desc}
                    </p>

                    {/* Hover-reveal affordance — makes the card's click target legible */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.875rem',
                      fontSize: '0.78rem', fontWeight: 700, color: CL.action, fontFamily: LP.heading,
                      opacity: (isHovered || isLead) ? 1 : 0,
                      transform: (isHovered || isLead) ? 'translateX(0)' : 'translateX(-6px)',
                      transition: 'opacity 0.25s cubic-bezier(0.16,1,0.3,1), transform 0.25s cubic-bezier(0.16,1,0.3,1)',
                    }}>
                      {isLead ? 'See the programme' : 'Join the group'}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── MENTORSHIP ── */}
      {/* The headline offer. Sits on the Forest side of the page on purpose —
          it's the one block that should stop the scroll, and the dark panel
          between two Parchment sections does that without a louder treatment.
          Its own seams are short gradient strips rather than the full
          320px Hero seam, which would dwarf the section itself. */}
      <div aria-hidden="true" style={{ height: isMobile ? '90px' : '140px', background: `linear-gradient(to bottom, ${CL.bg} 0%, #EDE3CA 28%, #DEC997 50%, #635624 70%, ${C.bg} 100%)` }} />

      <section id="mentorship" style={{ padding: isMobile ? '3.5rem 1rem' : '5.5rem 2rem', position: 'relative', overflow: 'hidden', background: C.bg }}>
        <div style={{
          position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
          width: 'min(900px, 120vw)', height: '520px',
          background: `radial-gradient(ellipse, rgba(${nerveRgb},0.14) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, black 20%, transparent 100%)',
        }} />

        <div style={{ maxWidth: '1060px', margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '2.75rem' : '3.5rem' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: C.action, border: `1px solid ${C.actionHi}`,
              borderRadius: '2rem', padding: '0.4rem 1.125rem', marginBottom: '1.5rem',
              fontSize: '0.68rem', fontFamily: LP.heading, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: C.bg,
              boxShadow: `0 0 32px rgba(${nerveRgb},0.45)`,
            }}>
              Most Popular · Mentorship
            </div>
            <h2 style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
              fontWeight: 800, marginBottom: '1rem',
              letterSpacing: '-0.03em', fontFamily: LP.heading, color: C.text, lineHeight: 1.1,
            }}>
              Learn to invest,<br />
              <span style={{
                background: `linear-gradient(135deg, ${C.actionHi} 0%, ${C.action} 50%, ${C.actionLo} 100%)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                not to trade tips.
              </span>
            </h2>
            <p style={{ color: C.text, fontSize: '1rem', lineHeight: 1.75, fontWeight: 300, fontFamily: LP.body, maxWidth: '580px', margin: '0 auto', opacity: 0.82 }}>
              12 structured modules taking you from &ldquo;what is an exchange&rdquo; to a real,
              conviction-weighted portfolio, plus direct access to me inside the
              community for the questions a course can&rsquo;t answer.
            </p>
          </div>

          {/* Four tracks */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : isMd ? 'repeat(2, 1fr)' : '1fr',
            gap: '1rem', marginBottom: isMobile ? '2.5rem' : '3rem',
          }}>
            {MENTORSHIP_TRACKS.map((t) => (
              <div key={t.num} style={{
                background: `rgba(${nerveRgb},0.05)`,
                border: `1px solid ${C.border}`,
                borderRadius: '1rem', padding: '1.5rem 1.375rem',
                transition: 'background 0.22s, border-color 0.22s, transform 0.22s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = `rgba(${nerveRgb},0.1)`; e.currentTarget.style.borderColor = C.borderHi; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = `rgba(${nerveRgb},0.05)`; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontFamily: LP.data, fontSize: '0.7rem', fontWeight: 700, color: C.action, letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                  MODULES {t.num}
                </div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: C.text, fontFamily: LP.heading, letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
                  {t.title}
                </h3>
                <p style={{ color: C.text, fontSize: '0.82rem', lineHeight: 1.6, fontWeight: 300, fontFamily: LP.body, margin: 0, opacity: 0.72 }}>
                  {t.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Outcomes + price/CTA panel */}
          <div style={{
            background: `linear-gradient(145deg, rgba(${nerveRgb},0.1) 0%, rgba(${nerveRgb},0.04) 100%)`,
            border: `1.5px solid ${C.borderHi}`, borderRadius: '1.75rem',
            padding: isMobile ? '2rem 1.5rem' : '2.75rem',
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'minmax(0, 1.35fr) minmax(0, 1fr)' : '1fr',
            gap: isMobile ? '2rem' : '3rem',
            alignItems: 'center',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            boxShadow: `0 32px 80px rgba(0,0,0,0.3)`,
          }}>
            <div>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.action, fontFamily: LP.heading, marginBottom: '1.25rem' }}>
                By the end, you can
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: isMd ? 'repeat(2, 1fr)' : '1fr', gap: '0.75rem 1.5rem' }}>
                {MENTORSHIP_OUTCOMES.map((o) => (
                  <li key={o} style={{ fontSize: '0.86rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.action} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '0.22rem' }}><polyline points="20 6 9 17 4 12" /></svg>
                    <span style={{ color: C.text, opacity: 0.88, lineHeight: 1.55, fontWeight: 400, fontFamily: LP.body }}>{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ textAlign: isDesktop ? 'right' : 'left' }}>
              <div style={{ fontSize: '2.75rem', fontWeight: 800, color: C.text, letterSpacing: '-0.04em', lineHeight: 1, fontFamily: LP.data }}>
                ₹2,199
              </div>
              <div style={{ color: C.textMuted, fontSize: '0.78rem', margin: '0.35rem 0 1.5rem', fontFamily: LP.body }}>
                one-time · lifetime access
              </div>

              <Link to="/mentorship?source=landing" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
                background: C.action, color: '#fff',
                padding: '1rem 2.25rem', borderRadius: '0.875rem',
                textDecoration: 'none', fontWeight: 700, fontFamily: LP.heading, fontSize: '0.95rem',
                boxShadow: `0 0 44px rgba(${nerveRgb},0.45)`, transition: 'all 0.22s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = C.actionHi; e.currentTarget.style.boxShadow = `0 0 64px rgba(${nerveRgb},0.6)`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.action; e.currentTarget.style.boxShadow = `0 0 44px rgba(${nerveRgb},0.45)`; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Start the mentorship
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </Link>

              <p style={{ color: C.textMuted, fontSize: '0.74rem', margin: '1rem 0 0', fontFamily: LP.body, lineHeight: 1.6 }}>
                Includes community access and the full slide decks
              </p>
            </div>
          </div>
        </div>
      </section>

      <div aria-hidden="true" style={{ height: isMobile ? '90px' : '140px', background: `linear-gradient(to bottom, ${C.bg} 0%, #635624 30%, #DEC997 50%, #EDE3CA 72%, ${CL.bg} 100%)` }} />

      {/* ── TESTIMONIALS ── */}
      {/* Renders only once VIDEO_TESTIMONIALS or TESTIMONIALS has real entries
          (see their shapes near the top of the file), so nothing invented can
          ship by accident. Sits right under the Mentorship section because
          that's what the proof is mostly for. */}
      {(VIDEO_TESTIMONIALS.length > 0 || TESTIMONIALS.length > 0) && (
        <section id="testimonials" style={{ padding: isMobile ? '3.5rem 1rem' : '5.5rem 2rem', position: 'relative' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '2.5rem' : '3.5rem' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: `rgba(${nerveRgbL},0.08)`, border: `1px solid ${CL.borderHi}`,
                borderRadius: '2rem', padding: '0.4rem 1.125rem', marginBottom: '1.5rem',
                fontSize: '0.68rem', fontFamily: LP.heading, fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase', color: CL.action,
              }}>
                Testimonials
              </div>
              <h2 style={{
                fontSize: 'clamp(1.875rem, 4vw, 2.875rem)',
                fontWeight: 800, letterSpacing: '-0.03em', fontFamily: LP.heading, color: CL.text,
                margin: '0 0 0.75rem', lineHeight: 1.1,
              }}>
                In their own words
              </h2>
              <p style={{ color: CL.text, fontSize: '0.95rem', fontWeight: 300, fontFamily: LP.body, opacity: 0.75, margin: 0 }}>
                What mentorship members and the community say.
              </p>
            </div>

            {/* Video testimonials. One entry gets a single large centred
                player; more than one falls into a grid. `preload="metadata"`
                keeps the page light while still giving the player a duration,
                and playsInline stops iOS hijacking into fullscreen. */}
            {VIDEO_TESTIMONIALS.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: VIDEO_TESTIMONIALS.length === 1
                  ? '1fr'
                  : (isMd ? 'repeat(2, 1fr)' : '1fr'),
                gap: isMobile ? '1.5rem' : '2rem',
                maxWidth: VIDEO_TESTIMONIALS.length === 1 ? '680px' : 'none',
                margin: VIDEO_TESTIMONIALS.length === 1 ? '0 auto' : 0,
                marginBottom: TESTIMONIALS.length > 0 ? (isMobile ? '2.5rem' : '3.5rem') : 0,
              }}>
                {VIDEO_TESTIMONIALS.map((v) => (
                  <figure key={v.src} style={{
                    margin: 0, background: CL.surface,
                    border: `1px solid ${CL.border}`, borderRadius: '1.25rem',
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                    transition: 'transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s, border-color 0.22s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 56px rgba(0,0,0,0.16)'; e.currentTarget.style.borderColor = CL.borderHi; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = CL.border; }}
                  >
                    {/* A lone video keeps its own shape; in a grid they share
                        one 16/9 box (with objectFit: contain below) so cards of
                        differently-shaped source footage still line up. */}
                    <div style={{
                      position: 'relative', background: '#0A1812',
                      aspectRatio: VIDEO_TESTIMONIALS.length === 1 ? (v.aspect || '16 / 9') : '16 / 9',
                    }}>
                      <video
                        src={v.src}
                        poster={v.poster}
                        controls
                        preload="metadata"
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                      >
                        Your browser does not support embedded video.
                      </video>
                    </div>

                    <figcaption style={{ padding: isMobile ? '1.25rem' : '1.5rem 1.625rem' }}>
                      {v.quote && (
                        <blockquote style={{ margin: '0 0 1.125rem', color: CL.text, fontSize: '0.92rem', lineHeight: 1.7, fontWeight: 300, fontFamily: LP.body, opacity: 0.88 }}>
                          &ldquo;{v.quote}&rdquo;
                        </blockquote>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                        <div style={{
                          width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                          background: `rgba(${nerveRgbL},0.12)`, border: `1px solid rgba(${nerveRgbL},0.25)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: CL.action, fontFamily: LP.heading, fontWeight: 700, fontSize: '0.8rem',
                        }}>
                          {v.name?.trim().charAt(0).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.86rem', fontWeight: 700, color: CL.text, fontFamily: LP.heading, letterSpacing: '-0.01em' }}>{v.name}</div>
                          {v.role && <div style={{ fontSize: '0.74rem', color: CL.textMuted, fontFamily: LP.body }}>{v.role}</div>}
                        </div>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            {/* Written quotes */}
            {TESTIMONIALS.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : isMd ? 'repeat(2, 1fr)' : '1fr',
                gap: '1.25rem',
              }}>
                {TESTIMONIALS.map((t, i) => (
                  <figure key={i} style={{
                    margin: 0, background: CL.surface,
                    border: `1px solid ${CL.border}`, borderRadius: '1.25rem',
                    padding: '1.75rem 1.625rem',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                    display: 'flex', flexDirection: 'column', gap: '1.125rem',
                    transition: 'transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s, border-color 0.22s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 16px 44px rgba(0,0,0,0.14)'; e.currentTarget.style.borderColor = CL.borderHi; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = CL.border; }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill={CL.action} opacity="0.3" aria-hidden="true" style={{ flexShrink: 0 }}>
                      <path d="M9.5 5C6.46 5 4 7.46 4 10.5c0 2.76 2.02 5.04 4.66 5.44L7 21h3.2l3.05-7.6c.5-1.24.75-2.3.75-3.19C14 7.3 12.04 5 9.5 5zm9 0C15.46 5 13 7.46 13 10.5c0 2.76 2.02 5.04 4.66 5.44L16 21h3.2l3.05-7.6c.5-1.24.75-2.3.75-3.19C23 7.3 21.04 5 18.5 5z" />
                    </svg>
                    <blockquote style={{ margin: 0, color: CL.text, fontSize: '0.9rem', lineHeight: 1.7, fontWeight: 300, fontFamily: LP.body, opacity: 0.88, flex: 1 }}>
                      {t.quote}
                    </blockquote>
                    <figcaption style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', borderTop: `1px solid ${CL.border}`, paddingTop: '1.125rem' }}>
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                        background: `rgba(${nerveRgbL},0.12)`, border: `1px solid rgba(${nerveRgbL},0.25)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: CL.action, fontFamily: LP.heading, fontWeight: 700, fontSize: '0.8rem',
                      }}>
                        {t.name?.trim().charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.84rem', fontWeight: 700, color: CL.text, fontFamily: LP.heading, letterSpacing: '-0.01em' }}>{t.name}</div>
                        {t.role && <div style={{ fontSize: '0.72rem', color: CL.textMuted, fontFamily: LP.body }}>{t.role}</div>}
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: isMobile ? '4rem 1rem' : '6rem 2rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: '800px', height: '400px',
          background: `radial-gradient(ellipse, rgba(${nerveRgbL},0.07) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative' }}>
          <div ref={stepsHeadRef} style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: `rgba(${nerveRgbL},0.08)`, border: `1px solid ${CL.borderHi}`,
              borderRadius: '2rem', padding: '0.4rem 1.125rem', marginBottom: '1.5rem',
              fontSize: '0.68rem', fontFamily: LP.heading, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: CL.action,
            }}>
              How It Works
            </div>
            <h2 style={{
              fontSize: 'clamp(1.875rem, 4vw, 2.875rem)',
              fontWeight: 800, letterSpacing: '-0.03em', fontFamily: LP.heading, color: CL.text,
            }}>
              Three steps to get started
            </h2>
          </div>

          {/* Steps with stagger */}
          <div ref={stepsGridRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '2rem', position: 'relative' }}>
            {/* Connecting line (desktop) */}
            {!isMobile && (
              <div style={{
                position: 'absolute', top: '2.5rem', left: '16.5%', right: '16.5%', height: '1px',
                background: `linear-gradient(90deg, transparent, ${CL.border}, ${CL.border}, transparent)`,
                pointerEvents: 'none',
              }} />
            )}

            {STEPS.map((step, i) => {
              const isH = hoveredStep === i;
              return (
                <div key={step.num}
                  className="gsap-card"
                  style={{ textAlign: 'center', cursor: 'default' }}
                  onMouseEnter={() => setHoveredStep(i)}
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  {/* Step circle */}
                  <div style={{
                    width: '5rem', height: '5rem', borderRadius: '50%',
                    margin: '0 auto 1.75rem',
                    background: isH ? CL.action : `rgba(${nerveRgbL},0.08)`,
                    border: isH ? `2px solid ${CL.actionHi}` : `2px solid ${CL.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: isH ? `0 0 36px rgba(${nerveRgbL},0.45), 0 0 60px rgba(${nerveRgbL},0.2)` : 'none',
                    transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
                    position: 'relative', zIndex: 1,
                    transform: isH ? 'scale(1.08)' : 'scale(1)',
                  }}>
                    <span style={{
                      fontFamily: LP.data, fontSize: '1.1rem', fontWeight: 700,
                      color: isH ? (CL.bg) : CL.action,
                      transition: 'color 0.25s',
                    }}>
                      {step.num}
                    </span>

                    {/* Outer ring on hover */}
                    {isH && (
                      <div style={{
                        position: 'absolute', inset: '-8px', borderRadius: '50%',
                        border: `1px solid rgba(${nerveRgbL},0.3)`,
                        animation: 'fadeInUp 0.25s ease forwards',
                      }} />
                    )}
                  </div>

                  <h3 style={{
                    fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.625rem',
                    color: CL.text, fontFamily: LP.heading, letterSpacing: '-0.02em',
                  }}>
                    {step.title}
                  </h3>
                  <p style={{ color: CL.text, fontSize: '0.88rem', lineHeight: 1.65, fontWeight: 300, fontFamily: LP.body, maxWidth: '240px', margin: '0 auto', opacity: 0.8 }}>
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PRICING (commented out 2026-10-23) ──────────────────────────────
          The Mentorship section above already carries the price and the CTA, so
          a second plans table on the same page competed with it. The standalone
          /pricing route still renders the full table (pages/Pricing.js), and the
          nav and footer both link there.

          To restore: lift the block below back out of this comment, restore its
          inner comment delimiters (they were changed to parentheses because a
          nested closing delimiter would end this comment early), and un-comment
          PRICING, PLAN_ICONS, hoveredPlan, priceHeadRef and priceGridRef near
          the top of the file.

      ( ── PRICING ── )
      <section id="pricing" style={{ padding: isMobile ? '4rem 1rem' : '6rem 2rem', position: 'relative', background: sectionAltLight }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '120px', background: `linear-gradient(to bottom, ${CL.bg}, transparent)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '120px', background: `linear-gradient(to top, ${CL.bg}, transparent)`, pointerEvents: 'none' }} />
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `radial-gradient(${CL.border} 1px, transparent 1px)`,
          backgroundSize: '32px 32px', opacity: 0.5,
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative' }}>
          <div ref={priceHeadRef} style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: `rgba(${nerveRgbL},0.08)`, border: `1px solid ${CL.borderHi}`,
              borderRadius: '2rem', padding: '0.4rem 1.125rem', marginBottom: '1.5rem',
              fontSize: '0.68rem', fontFamily: LP.heading, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: CL.action,
            }}>
              Pricing
            </div>
            <h2 style={{
              fontSize: 'clamp(2rem, 4.5vw, 3rem)',
              fontWeight: 800, marginBottom: '0.75rem',
              letterSpacing: '-0.03em', fontFamily: LP.heading, color: CL.text, lineHeight: 1.1,
            }}>
              Simple, transparent pricing
            </h2>
            <p style={{ color: CL.text, fontSize: '0.95rem', fontWeight: 300, fontFamily: LP.body, opacity: 0.75 }}>
              The community is free. Mentorship is a one-time ₹2,199. No hidden fees.
            </p>
          </div>

          ( Pricing cards with stagger )
          <div ref={priceGridRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            {PRICING.map((plan) => {
              const isPlanHovered = hoveredPlan === plan.name;
              return (
              <div key={plan.name} className="gsap-card pricing-card" tabIndex={0}
                onMouseEnter={() => setHoveredPlan(plan.name)}
                onMouseLeave={() => setHoveredPlan(null)}
                style={{
                background: plan.highlight
                  ? `linear-gradient(145deg, ${CL.surface} 0%, ${CL.surfaceHi} 100%)`
                  : (isPlanHovered ? CL.surfaceHi : CL.surface),
                border: plan.highlight ? `1.5px solid ${CL.action}` : `1px solid ${isPlanHovered ? CL.borderHi : CL.border}`,
                borderRadius: '1.75rem',
                padding: plan.highlight ? '3rem 2.5rem' : '2.5rem 2.25rem',
                position: 'relative',
                boxShadow: plan.highlight
                  ? `0 0 60px rgba(${nerveRgbL},0.2), 0 24px 64px rgba(0,0,0,0.25)`
                  : (isPlanHovered ? `0 16px 48px rgba(0,0,0,0.16)` : `0 4px 24px rgba(0,0,0,0.1)`),
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                transform: isPlanHovered && !plan.highlight ? 'translateY(-6px)' : 'translateY(0)',
                transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s, background 0.22s, border-color 0.22s',
              }}>
                ( Pro card glow halo )
                {plan.highlight && (
                  <div style={{
                    position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
                    width: '300px', height: '200px',
                    background: `radial-gradient(ellipse, rgba(${nerveRgbL},0.15) 0%, transparent 70%)`,
                    pointerEvents: 'none',
                  }} />
                )}

                ( Animated top border line on Pro card )
                {plan.highlight && (
                  <div className="pro-top-glow" style={{
                    position: 'absolute', top: 0, left: '15%', right: '15%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${CL.action}, ${CL.actionHi}, ${CL.action}, transparent)`,
                    borderRadius: '0 0 4px 4px',
                  }} />
                )}

                {plan.badge && (
                  <div className="badge-shimmer" style={{
                    position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
                    color: CL.bg,
                    fontSize: '0.65rem', fontWeight: 700,
                    padding: '0.3rem 1.25rem', borderRadius: '2rem', whiteSpace: 'nowrap',
                    letterSpacing: '0.08em', fontFamily: LP.heading, textTransform: 'uppercase',
                    boxShadow: `0 4px 16px rgba(${nerveRgbL},0.4)`,
                  }}>
                    {plan.badge}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '9px', flexShrink: 0,
                    background: `rgba(${nerveRgbL},0.1)`, border: `1px solid rgba(${nerveRgbL},0.2)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: CL.action,
                  }}>
                    {PLAN_ICONS[plan.icon]}
                  </div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: CL.action, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: LP.heading }}>
                    {plan.name}
                  </div>
                </div>

                <div style={{ marginBottom: '0.5rem', lineHeight: 1, display: 'flex', alignItems: 'flex-end', gap: '0.25rem' }}>
                  <span style={{ fontSize: '3.25rem', fontWeight: 800, color: CL.text, letterSpacing: '-0.04em', fontFamily: LP.data, lineHeight: 1 }}>
                    {plan.price}
                  </span>
                  <span style={{ color: CL.textMuted, fontSize: '0.875rem', marginBottom: '0.5rem', fontFamily: LP.body }}>{plan.period}</span>
                </div>

                <div style={{ color: CL.text, fontSize: '0.85rem', marginBottom: plan.addon ? '0.75rem' : '2rem', fontWeight: 300, fontFamily: LP.body, lineHeight: 1.5, opacity: 0.75 }}>
                  {plan.desc}
                </div>

                {plan.addon && (
                  <div style={{
                    marginBottom: '2rem', display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                    background: 'rgba(93,184,96,0.08)', border: '1px solid rgba(93,184,96,0.22)',
                    borderRadius: '0.5rem', padding: '0.375rem 0.875rem',
                    fontSize: '0.72rem', color: '#5DB860', fontWeight: 600, fontFamily: LP.heading,
                  }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                    {plan.addon}
                  </div>
                )}

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2.5rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                  {plan.included.map((item) => (
                    <li key={item} style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5DB860" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '0.15rem' }}><polyline points="20 6 9 17 4 12"/></svg>
                      <span style={{ color: CL.text, opacity: 0.94, lineHeight: 1.5, fontWeight: 400, fontFamily: LP.body }}>{item}</span>
                    </li>
                  ))}
                  {plan.notIncluded?.length > 0 && (
                    <>
                      <li style={{ height: '1px', background: CL.border, margin: '0.35rem 0' }} />
                      {plan.notIncluded.map((item) => (
                        <li key={item} style={{ fontSize: '0.855rem', display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CL.bear} strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '0.15rem' }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          <span style={{ color: CL.text, opacity: 0.5, lineHeight: 1.5, fontWeight: 400, fontFamily: LP.body }}>{item}</span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>

                <button onClick={() => setShowLogin(true)} style={{
                  display: 'block', width: '100%', textAlign: 'center',
                  background: plan.highlight ? CL.action : `rgba(${nerveRgbL},0.07)`,
                  color: plan.highlight ? (CL.bg) : CL.text,
                  border: plan.highlight ? `1px solid ${CL.action}` : `1px solid ${CL.border}`,
                  cursor: 'pointer',
                  padding: '1rem', borderRadius: '0.875rem',
                  fontWeight: 700, fontFamily: LP.heading,
                  fontSize: '0.9rem', letterSpacing: '-0.01em',
                  transition: 'all 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = plan.highlight ? CL.actionHi : `rgba(${nerveRgbL},0.12)`; e.currentTarget.style.borderColor = CL.borderHi; e.currentTarget.style.transform = 'scale(1.015)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = plan.highlight ? CL.action : `rgba(${nerveRgbL},0.07)`; e.currentTarget.style.borderColor = plan.highlight ? CL.action : CL.border; e.currentTarget.style.transform = 'scale(1)'; }}>
                  {plan.cta} →
                </button>
                {plan.highlight && (
                  <p style={{ textAlign: 'center', color: CL.textMuted, fontSize: '0.7rem', marginTop: '0.625rem', fontFamily: LP.body, opacity: 0.7 }}>
                    Sign up free now and we'll notify you the moment Pro opens.
                  </p>
                )}
              </div>
              );
            })}
          </div>

          ( Mentorship add-on )
          <div style={{
            background: false
              ? `linear-gradient(135deg, rgba(${nerveRgbL},0.08) 0%, rgba(${nerveRgbL},0.04) 100%)`
              : `linear-gradient(135deg, rgba(${nerveRgbL},0.06) 0%, rgba(${nerveRgbL},0.03) 100%)`,
            border: `1.5px solid ${CL.borderHi}`, borderRadius: '1.5rem', padding: '2.5rem',
            position: 'relative', display: 'flex', gap: '2rem', flexWrap: 'wrap',
            alignItems: 'center', justifyContent: 'space-between',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          }}>
            <div style={{
              position: 'absolute', top: '-14px', left: '2rem',
              background: CL.actionLo, color: CL.bg,
              fontSize: '0.65rem', fontWeight: 700, padding: '0.3rem 1rem',
              borderRadius: '2rem', letterSpacing: '0.08em', fontFamily: LP.heading, textTransform: 'uppercase',
            }}>
              Most Popular · One-time
            </div>
            <div style={{ flex: '1 1 320px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: `rgba(${nerveRgbL},0.1)`, border: `1px solid rgba(${nerveRgbL},0.2)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: CL.action, flexShrink: 0,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                </div>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: CL.action, fontFamily: LP.heading, letterSpacing: '-0.02em' }}>
                  Mentorship
                </span>
              </div>
              <p style={{ color: CL.text, fontSize: '0.875rem', lineHeight: 1.65, margin: 0, fontWeight: 300, fontFamily: LP.body, opacity: 0.8 }}>
                All 12 modules, the full slide decks, WhatsApp community access and
                direct answers from me. One payment, lifetime access.{' '}
                <a href="#mentorship" style={{ color: CL.action, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '3px' }}>See what&rsquo;s covered</a>.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1.25rem', flexShrink: 0 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '2.75rem', fontWeight: 800, color: CL.text, letterSpacing: '-0.04em', lineHeight: 1, fontFamily: LP.data }}>₹2,199</div>
                <div style={{ color: CL.textMuted, fontSize: '0.78rem', marginTop: '0.25rem', fontFamily: LP.body }}>one-time payment</div>
              </div>
              <Link to="/mentorship?source=landing" style={{
                background: `linear-gradient(135deg, ${CL.action}, ${CL.actionLo})`,
                color: CL.bg, padding: '0.875rem 2rem',
                borderRadius: '0.75rem', textDecoration: 'none', fontWeight: 700,
                fontFamily: LP.heading, fontSize: '0.875rem',
                boxShadow: `0 0 28px rgba(${nerveRgbL},0.3)`, whiteSpace: 'nowrap', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = CL.actionHi; e.currentTarget.style.boxShadow = `0 0 44px rgba(${nerveRgbL},0.45)`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${CL.action}, ${CL.actionLo})`; e.currentTarget.style.boxShadow = `0 0 28px rgba(${nerveRgbL},0.3)`; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Start the mentorship →
              </Link>
            </div>
          </div>

          <p style={{ textAlign: 'center', color: CL.textMuted, fontSize: '0.78rem', marginTop: '2rem', fontFamily: LP.heading, letterSpacing: '0.02em' }}>
            All prices in INR · Pro plan billed monthly · Cancel anytime
          </p>
        </div>
      </section>


      )


      */}

      {/* ── FAQ ── */}
      <section style={{ padding: isMobile ? '3.5rem 1rem' : '5rem 2rem', position: 'relative' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: `rgba(${nerveRgbL},0.08)`, border: `1px solid ${CL.borderHi}`,
              borderRadius: '2rem', padding: '0.4rem 1.125rem', marginBottom: '1.5rem',
              fontSize: '0.68rem', fontFamily: LP.heading, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: CL.action,
            }}>
              FAQ
            </div>
            <h2 style={{
              fontSize: 'clamp(1.875rem, 4vw, 2.875rem)',
              fontWeight: 800, letterSpacing: '-0.03em', fontFamily: LP.heading, color: CL.text,
            }}>
              Questions, answered
            </h2>
          </div>

          <div>
            {FAQS.map((item, i) => (
              <FAQItem
                key={item.q}
                item={item}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                C={CL}
                nerveRgb={nerveRgbL}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── SEAM: FAQ → Final CTA ── */}
      {/* Mirror of the Hero/Ticker seam above — same multi-stop gradient
          (reversed) and faint atmospheric glow, no dot texture, no ticker
          (there isn't one on this side), running Parchment → Forest instead. */}
      <div aria-hidden="true" style={{
        position: 'relative', overflow: 'hidden',
        height: isMobile ? '200px' : '320px',
        background: `linear-gradient(to bottom, ${SEAM_STOPS_REVERSE.map(([p, c]) => `${c} ${p}%`).join(', ')})`,
      }}>
        <div style={{
          position: 'absolute', bottom: '30%', left: '50%', transform: 'translateX(-50%)',
          width: 'min(1500px, 170vw)', height: '55%',
          background: `radial-gradient(ellipse 60% 55% at 50% 50%, rgba(${nerveRgb},0.08) 0%, transparent 45%)`,
          pointerEvents: 'none',
        }} />
      </div>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: isMobile ? '3.5rem 1rem 5rem' : '5rem 2rem 7rem', textAlign: 'center', position: 'relative', overflow: 'hidden', background: C.bg }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: '700px', height: '400px',
          background: `radial-gradient(ellipse, rgba(${nerveRgb},0.12) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <div ref={ctaRef} style={{
          maxWidth: '580px', margin: '0 auto', position: 'relative',
          background: isDark
            ? `linear-gradient(145deg, rgba(${nerveRgb},0.09) 0%, rgba(${nerveRgb},0.04) 100%)`
            : `linear-gradient(145deg, rgba(${nerveRgb},0.07) 0%, rgba(${nerveRgb},0.03) 100%)`,
          border: `1.5px solid ${C.borderHi}`, borderRadius: '2.5rem',
          padding: isMobile ? '3rem 1.75rem' : '4rem 3rem',
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          boxShadow: `0 32px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(${nerveRgb},0.1)`,
        }}>
          <div style={{
            position: 'absolute', top: 0, left: '20%', right: '20%', height: '1px',
            background: `linear-gradient(90deg, transparent, rgba(${nerveRgb},0.5), transparent)`,
          }} />

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.75rem' }}>
            <div style={{
              width: '60px', height: '60px', borderRadius: '16px',
              background: `rgba(${nerveRgb},0.12)`, border: `1px solid rgba(${nerveRgb},0.25)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 0 40px rgba(${nerveRgb},0.3)`,
            }}>
              <img src="/logo.png" alt="MarketNerve" style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover' }} />
            </div>
          </div>

          <h2 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.375rem)',
            fontWeight: 800, marginBottom: '0.875rem',
            letterSpacing: '-0.03em', fontFamily: LP.heading, color: C.action, lineHeight: 1.1,
          }}>
            Join {COMMUNITY_SIZE}+ investors
          </h2>
          <p style={{ color: C.text, marginBottom: '2.25rem', fontSize: '1rem', lineHeight: 1.7, fontWeight: 300, fontFamily: LP.body, opacity: 0.8 }}>
            The daily briefing, the mentorship, the resources and the events, all
            run out of one WhatsApp community. It&rsquo;s free to join.
          </p>

          <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
            background: '#25D366', color: '#07251A',
            padding: '1.1rem 2.5rem', borderRadius: '0.875rem',
            textDecoration: 'none', fontWeight: 700,
            fontFamily: LP.heading, fontSize: '1rem', letterSpacing: '-0.01em',
            boxShadow: '0 0 52px rgba(37,211,102,0.45)', transition: 'all 0.22s',
            marginBottom: '1.75rem',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1FBF5C'; e.currentTarget.style.boxShadow = '0 0 72px rgba(37,211,102,0.62)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#25D366'; e.currentTarget.style.boxShadow = '0 0 52px rgba(37,211,102,0.45)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
            {ICONS.whatsapp}
            Join the WhatsApp Community
          </a>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', margin: '0 auto 1.75rem', maxWidth: '280px' }}>
            <span style={{ flex: 1, height: '1px', background: C.border }} />
            <span style={{ fontSize: '0.68rem', color: C.textMuted, fontFamily: LP.heading, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>or</span>
            <span style={{ flex: 1, height: '1px', background: C.border }} />
          </div>

          <Magnet padding={90} strength={5}>
            <button onClick={() => setShowLogin(true)} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
              background: `rgba(${nerveRgb},0.08)`, color: C.text,
              padding: '1rem 2.25rem', borderRadius: '0.875rem',
              border: `1px solid ${C.borderHi}`, cursor: 'pointer', fontWeight: 700,
              fontFamily: LP.heading, fontSize: '0.95rem', letterSpacing: '-0.01em',
              transition: 'all 0.22s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `rgba(${nerveRgb},0.14)`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = `rgba(${nerveRgb},0.08)`; e.currentTarget.style.transform = 'translateY(0)'; }}>
              <svg width="20" height="20" viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
                <path d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="currentColor"/>
              </svg>
              Sign in with Google for the platform
            </button>
          </Magnet>

          <p style={{ marginTop: '1.25rem', color: C.text, fontSize: '0.75rem', fontFamily: LP.body, opacity: 0.5 }}>
            No credit card required · Free forever plan available
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        borderTop: `1px solid ${C.border}`,
        // Solid Forest base (the page root is Parchment now, so the footer
        // needs its own opaque backdrop) with the original faint gold wash
        // layered on top.
        background: `linear-gradient(rgba(${nerveRgb},0.025), rgba(${nerveRgb},0.025)), ${C.bg}`,
        fontFamily: LP.heading,
      }}>
        {/* Main footer grid */}
        <div style={{
          maxWidth: '1160px', margin: '0 auto',
          padding: isMobile ? '3rem 1.25rem 2rem' : '4rem 2.5rem 2.5rem',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : isDesktop ? '2fr 1fr 1fr 1fr' : '1fr 1fr 1fr',
          gap: isMobile ? '2.5rem 1.5rem' : '2rem 3rem',
        }}>

          {/* Brand column */}
          <div style={{ gridColumn: isMobile ? '1 / -1' : '1 / 2' }}>
            <img src="/wordmark.png" alt="MarketNerve" style={{ height: '32px', objectFit: 'contain', marginBottom: '1rem', display: 'block' }} />
            <p style={{ color: C.textMuted, fontSize: '0.8rem', lineHeight: 1.7, fontFamily: LP.body, margin: '0 0 1.25rem', maxWidth: '240px', fontWeight: 300 }}>
              A {COMMUNITY_SIZE}+ strong investing community. Daily market briefing,
              mentorship, resources and live events.
            </p>
            <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.35)',
              color: '#4AE08A', padding: '0.55rem 1rem', borderRadius: '0.6rem',
              textDecoration: 'none', fontSize: '0.78rem', fontWeight: 700,
              fontFamily: LP.heading, marginBottom: '1.25rem', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.2)'; e.currentTarget.style.borderColor = 'rgba(37,211,102,0.65)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.12)'; e.currentTarget.style.borderColor = 'rgba(37,211,102,0.35)'; }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12 0C5.373 0 0 5.373 0 12c0 2.136.558 4.142 1.535 5.88L0 24l6.335-1.652A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0"/></svg>
              Join on WhatsApp
            </a>
            <br />
            <a href="mailto:marketnervehq@gmail.com" style={{ color: C.actionLo, fontSize: '0.75rem', textDecoration: 'none', fontFamily: LP.body, display: 'inline-flex', alignItems: 'center', gap: '0.4rem', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = C.action}
              onMouseLeave={e => e.currentTarget.style.color = C.actionLo}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              marketnervehq@gmail.com
            </a>
          </div>

          {/* Community column — the Platform list it replaced is kept below,
              commented out, alongside the Features section it belonged with. */}
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.action, marginBottom: '1.25rem' }}>
              Community
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <li>
                <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  WhatsApp Community
                </a>
              </li>
              <li>
                <a href="#community" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  Daily Market Briefing
                </a>
              </li>
              <li>
                <Link to="/mentorship" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  Mentorship
                </Link>
              </li>
              <li>
                <a href="#community" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  Resources &amp; Events
                </a>
              </li>
              <li>
                <Link to="/dashboard" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  Dashboard
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal column */}
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.action, marginBottom: '1.25rem' }}>
              Legal
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms of Use', href: '/terms' },
                { label: 'Disclaimer', href: '/disclaimer' },
              ].map(({ label, href }) => (
                <li key={label}>
                  <Link to={href} style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = C.text}
                    onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company column */}
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.action, marginBottom: '1.25rem' }}>
              Company
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <li>
                <Link to="/about" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  About Us
                </Link>
              </li>
              <li>
                <a href="mailto:marketnervehq@gmail.com" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  Contact Us
                </a>
              </li>
              <li>
                <Link to="/pricing" style={{ color: C.textMuted, fontSize: '0.82rem', textDecoration: 'none', fontFamily: LP.body, fontWeight: 300, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = C.text}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                  Pricing
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer bottom bar */}
        <div style={{
          borderTop: `1px solid ${C.border}`,
          padding: isMobile ? '1.25rem 1.25rem' : '1.5rem 2.5rem',
          maxWidth: '1160px', margin: '0 auto',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '0.75rem',
        }}>
          <p style={{ color: C.textDim, fontSize: '0.7rem', margin: 0, fontFamily: LP.body, fontWeight: 300, lineHeight: 1.6 }}>
            Market data is for informational and research purposes only. Nothing on this platform constitutes financial advice.
          </p>
          <p style={{ color: C.textDim, fontSize: '0.7rem', margin: 0, fontFamily: LP.body, fontWeight: 300, whiteSpace: 'nowrap' }}>
            © 2026 MarketNerve. All rights reserved.
          </p>
        </div>
      </footer>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

    </div>
  );
}
